import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { groupMinistries, groupTitle } from "@/lib/ministries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HeartHandshake, CheckCircle2, Loader2, UserCircle, Clock } from "lucide-react";

function MinistryRow({ m, busy, onVolunteer, main = false }) {
  return (
    <li className={`p-5 flex flex-col sm:flex-row sm:items-center gap-3 ${main ? "bg-[#F7F5F0]" : "sm:pl-10"}`} data-testid={`portal-ministry-${m.id}`}>
      <div className="flex-1 min-w-0">
        <p className={main ? "font-serif text-lg font-bold text-[#1C241B]" : "font-medium text-[#1C241B]"}>{m.name}</p>
        {m.description && <p className="mt-1 text-sm text-[#526050]">{m.description}</p>}
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#7A8A78]">
          {m.leader && <span className="flex items-center gap-1"><UserCircle className="h-3.5 w-3.5" />{m.leader}</span>}
          {m.schedule && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{m.schedule}</span>}
        </div>
      </div>
      {m.joined ? (
        <Badge className="self-start sm:self-center bg-[#EAF2E8] text-[#2D5A27] border border-[#cfe3cb] hover:bg-[#EAF2E8] px-3 py-1.5"
          data-testid={`portal-ministry-joined-${m.id}`}>
          <CheckCircle2 className="h-4 w-4 mr-1.5" /> Joined
        </Badge>
      ) : (
        <Button onClick={() => onVolunteer(m)} disabled={busy === m.id} data-testid={`portal-ministry-volunteer-${m.id}`}
          className="self-start sm:self-center bg-[#D97706] hover:bg-[#B45309] text-white">
          {busy === m.id && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Volunteer
        </Button>
      )}
    </li>
  );
}

export const MyMinistries = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/me/ministries").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const volunteer = async (m) => {
    setBusy(m.id);
    try {
      await api.post(`/me/ministries/${m.id}/volunteer`);
      toast.success(`Thank you for volunteering for ${m.name}!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not volunteer");
    } finally { setBusy(null); }
  };

  const joined = items.filter((m) => m.joined).length;
  const props = { busy, onVolunteer: volunteer };

  return (
    <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden" data-testid="portal-ministries">
      <div className="flex items-center gap-2 p-5 border-b border-[#E5E0D8]">
        <HeartHandshake className="h-5 w-5 text-[#D97706]" />
        <h2 className="font-serif text-xl font-semibold">Ministries & Sub-committees</h2>
        <Badge variant="outline" className="ml-auto bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]" data-testid="portal-ministries-count">
          {joined} joined
        </Badge>
      </div>
      <ul className="divide-y divide-[#E5E0D8]">
        {groupMinistries(items).map((g) => (
          <li key={g.key} data-testid={`portal-ministry-group-${g.key}`}>
            <ul className="divide-y divide-[#E5E0D8]">
              {g.main ? <MinistryRow m={g.main} main {...props} /> : (
                <li className="px-5 py-3 bg-[#F7F5F0] font-serif text-lg font-bold text-[#1C241B]">{groupTitle(g)}</li>
              )}
              {g.subs.map((m) => <MinistryRow key={m.id} m={m} {...props} />)}
            </ul>
          </li>
        ))}
        {items.length === 0 && <li className="p-10 text-center text-[#7A8A78]">No ministries available right now.</li>}
      </ul>
    </Card>
  );
};
